import React from "react";
import { motion } from "framer-motion";
import { FaShieldAlt, FaHandsHelping, FaUserShield, FaStar, FaCertificate, FaClock } from "react-icons/fa";
import Head from "next/head";
import Link from "next/link";

const AbuseCounselling: React.FC = () => {
  return (
    <>
      <Head>
        <title>Abuse Counselling Calgary | Registered Psychologist</title>
        <meta
          name="description"
          content="Compassionate abuse counselling services in Calgary. Our registered psychologists provide support for healing and recovery."
        />
        <meta name="keywords" content="Abuse Counselling Calgary, Registered Psychologist Calgary, Calgary Therapy Services" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Calgary Abuse Counselling Services",
              "description": "Compassionate abuse counselling services in Calgary",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "317 37 Ave NE #209",
                "addressLocality": "Calgary",
                "addressRegion": "AB",
                "postalCode": "T2E 6P6",
                "addressCountry": "CA"
              },
              "telephone": "+1 (403) 244-0244",
              "priceRange": "$$",
              "serviceType": ["Abuse Counselling", "Therapy Services"]
            }
          `}
        </script>
      </Head>

      {/* Premium Hero Section */}
      <section className="relative text-white py-24 md:py-32 bg-gradient-to-r from-emerald-800 to-emerald-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto"
          >
            <div className="mb-8 flex justify-center items-center space-x-4">
              <div className="flex items-center bg-amber-100 text-emerald-800 px-6 py-2 rounded-full">
                <FaCertificate className="mr-2" />
                <span className="font-semibold">35+ Years Clinical Experience</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Trauma Recovery Specialists Serving Calgary Since 1989
            </h1>
            <p className="text-xl md:text-2xl font-light mb-8 max-w-3xl mx-auto">
              Evidence-Based Abuse Counselling Combining Modern Therapy Techniques with Decades of Clinical Wisdom
            </p>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
              <motion.a
                whileHover={{ scale: 1.05 }}
                href="/contact"
                className="bg-amber-400 text-emerald-900 px-8 py-4 rounded-xl text-lg font-bold shadow-lg hover:bg-amber-500 transition-colors flex items-center"
              >
                <FaClock className="mr-2" />
                Schedule Consultation
              </motion.a>
              <div className="text-center md:text-left">
                <p className="text-sm uppercase tracking-wide mb-1">Direct Line</p>
                <a href="tel:+14032440244" className="text-2xl font-bold hover:text-amber-300 transition-colors">
                  +1 (403) 244-0244
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Experience Badge Section */}
      <div className="max-w-7xl mx-auto px-6 mt-8 mb-16">
        <div className="bg-white shadow-2xl rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="bg-emerald-100 p-4 rounded-xl mr-4">
              <FaStar className="text-amber-500 text-3xl" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Why Choose Our Service?</h3>
              <p className="text-slate-600">35+ Years • 10,000+ Hours Clinical Experience • Certified Trauma Specialist</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Service Details */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="prose lg:prose-xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-16">
            Comprehensive Abuse & Trauma Recovery Program
          </h2>

          {/* Methodology Section */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-emerald-600"
            >
              <h3 className="text-2xl font-semibold text-emerald-700 mb-6 flex items-center">
                <FaHandsHelping className="mr-3 text-emerald-600" />
                Our Therapeutic Approach
              </h3>
              <p className="text-slate-700 leading-relaxed mb-6">
                Drawing from 35 years of clinical practice, we combine time-tested methods with cutting-edge trauma therapies:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-emerald-600 mr-3">▸</span>
                  <div>
                    <strong>Phase-Based Trauma Treatment:</strong><br/>
                    Stabilization → Processing → Reintegration
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 mr-3">▸</span>
                  <div>
                    <strong>Specialized Modalities:</strong><br/>
                    EMDR, Somatic Experiencing®, Trauma-Focused CBT
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-emerald-600 mr-3">▸</span>
                  <div>
                    <strong>Holistic Framework:</strong><br/>
                    Addressing mind, body, and relational impacts of trauma
                  </div>
                </li>
              </ul>
            </motion.div>

            <div className="space-y-12">
              <div className="bg-emerald-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-semibold text-emerald-700 mb-6">
                  Treatment Specializations
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    'Childhood Abuse', 'Domestic Violence', 'Sexual Trauma', 
                    'Elder Abuse', 'Workplace Abuse', 'Financial Abuse',
                    'Emotional Abuse', 'Spiritual Abuse'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full mr-3"></div>
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-amber-50 p-8 rounded-2xl">
                <h3 className="text-2xl font-semibold text-emerald-700 mb-6">
                  Client Success Stories
                </h3>
                <div className="space-y-4">
                  <blockquote className="border-l-4 border-emerald-600 pl-4">
                    <p className="text-slate-700 italic">
                      &ldquo;Murray&apos;s expertise in trauma recovery has been life-changing for me...&rdquo;
                    </p>
                    <footer className="mt-2 text-emerald-600">- Sarah, Calgary</footer>
                  </blockquote>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Service Features */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {[
              {
                title: "Trauma-Informed Care",
                content: "Specialized protocols for complex PTSD and dissociation",
                icon: <FaShieldAlt className="text-emerald-600 text-3xl" />
              },
              {
                title: "Family Recovery Programs",
                content: "Healing relational dynamics post-abuse",
                icon: <FaUserShield className="text-emerald-600 text-3xl" />
              },
              {
                title: "Legal Collaboration",
                content: "Court reports and expert testimony support",
                icon: <FaCertificate className="text-emerald-600 text-3xl" />
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                whileHover={{ y: -5 }}
                className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-semibold text-slate-800 mb-3">{feature.title}</h4>
                <p className="text-slate-600 leading-relaxed">{feature.content}</p>
              </motion.div>
            ))}
          </div>

          {/* Integrated Services Section */}
          <div className="bg-slate-50 p-8 rounded-2xl mb-20">
            <h3 className="text-2xl font-semibold text-emerald-700 mb-8">
              Complementary Support Services
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: "Addictions Recovery", link: "/services/addictions-counselling" },
                { name: "Family Mediation", link: "/services/mediation-and-consultation-counselling" },
                { name: "Anxiety Treatment", link: "/services/disorders-counselling" },
                { name: "Cultural Trauma", link: "/services/demographic-specific-counselling" }
              ].map((service, index) => (
                <Link
                  key={index}
                  href={service.link}
                  className="group flex items-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all"
                >
                  <span className="text-emerald-600 group-hover:text-emerald-700 transition-colors">
                    {service.name}
                  </span>
                  <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Premium CTA Section */}
        <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 rounded-2xl p-8 md:p-16 text-center text-white">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Begin Your Healing Journey Today
            </h3>
            <p className="text-lg mb-8 opacity-90">
              Take the first step towards recovery with our confidential consultation.<br/>
              Evening and weekend appointments available within 24 hours.
            </p>
            <div className="flex flex-col md:flex-row justify-center gap-6">
              <motion.a
                whileHover={{ scale: 1.05 }}
                href="/contact"
                className="bg-amber-400 text-emerald-900 px-8 py-4 rounded-xl text-lg font-bold shadow-lg hover:bg-amber-500 transition-colors"
              >
                Contact Us to Schedule
              </motion.a>
              <div className="md:border-l md:border-emerald-600 md:h-16" />
              <div className="space-y-2">
                <p className="text-sm uppercase tracking-wide">Direct Support Line</p>
                <a href="tel:+14032440244" className="text-2xl font-bold hover:text-amber-300 transition-colors">
                  +1 (403) 244-0244
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AbuseCounselling;